import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { VehicleIpvaLicensingService } from '../../../services/vehicle-ipva-licensing.service';
import { VehiclesService } from '../../../services/vehicles.service';
import {
  VehicleIpvaLicensingPaymentHistory,
  VehicleIpvaLicensingBankProvider,
  VehicleIpvaLicensingRequestPaymentPayload,
  VehicleIpvaLicensingRecordManualPayload
} from '../../../models/VehicleIpvaLicensing';
import { Vehicle } from '../../../models/Vehicle';
import { TableAction } from '../../../shared/components/table/table.component';

@Component({
  selector: 'app-vehicle-ipva-licensing-list',
  templateUrl: './vehicle-ipva-licensing-list.component.html',
  styleUrls: ['./vehicle-ipva-licensing-list.component.css']
})
export class VehicleIpvaLicensingListComponent implements OnInit {
  vehicleId: number | null = null;
  vehicle: Vehicle | null = null;
  history: VehicleIpvaLicensingPaymentHistory[] = [];
  bankProvider: VehicleIpvaLicensingBankProvider | null = null;
  loading = false;
  error: string | null = null;

  filterType: '' | 'IPVA' | 'Licenciamento' = '';
  filterYear: number | null = null;

  showModalBank = false;
  showModalManual = false;
  submittingBank = false;
  submittingManual = false;
  bankSuccessMessage: string | null = null;
  boletoUrl: string | null = null;

  tableTitle = 'Histórico IPVA e Licenciamento';
  showSearch = true;
  searchPlaceholder = 'Filtrar por status ou documento...';
  clickable = true;

  columns = [
    { titulo: 'Tipo', label: 'paymentType' },
    { titulo: 'Ano', label: 'referenceYear' },
    { titulo: 'Valor', label: 'amount', pipe: 'currency' },
    { titulo: 'Data Pagamento', label: 'paymentDate', pipe: 'date' },
    { titulo: 'Status', label: 'status', badge: true },
    { titulo: 'Documento', label: 'documentNumber' }
  ];

  actions: TableAction[] = [
    { label: 'Ver', icon: 'fas fa-eye', action: 'view', class: 'btn-view' },
    { label: 'Atualizar status', icon: 'fas fa-sync', action: 'refresh', class: 'btn-edit' }
  ];

  formBank: Partial<VehicleIpvaLicensingRequestPaymentPayload> = {
    paymentType: 'IPVA',
    referenceYear: new Date().getFullYear(),
    amount: 0,
    documentNumber: '',
    notes: ''
  };

  formManual: Partial<VehicleIpvaLicensingRecordManualPayload> = {
    paymentType: 'IPVA',
    referenceYear: new Date().getFullYear(),
    amount: 0,
    paymentDate: new Date().toISOString().slice(0, 10),
    documentNumber: '',
    notes: ''
  };

  currentYears: number[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ipvaLicensingService: VehicleIpvaLicensingService,
    private vehiclesService: VehiclesService
  ) {
    const y = new Date().getFullYear();
    this.currentYears = [y, y - 1, y - 2, y - 3, y - 4];
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const id = params.get('vehicleId');
      this.vehicleId = id ? +id : null;
      if (this.vehicleId) {
        this.loadVehicle();
        this.loadBankProvider();
        this.loadHistory();
      } else {
        this.error = 'Veículo não informado.';
      }
    });
  }

  loadVehicle(): void {
    if (!this.vehicleId) return;
    this.vehiclesService.getById(this.vehicleId).subscribe({
      next: v => (this.vehicle = v),
      error: () => (this.vehicle = null)
    });
  }

  loadBankProvider(): void {
    this.ipvaLicensingService.getBankProvider().subscribe({
      next: p => (this.bankProvider = p),
      error: () => (this.bankProvider = { isAvailable: false, providerName: 'Nenhum' })
    });
  }

  loadHistory(): void {
    if (!this.vehicleId) return;
    this.loading = true;
    this.error = null;

    const next = (list: VehicleIpvaLicensingPaymentHistory[]) => {
      this.history = this.applyFilters(list);
      this.loading = false;
    };
    const err = (e: any) => {
      this.error = e?.message || 'Erro ao carregar histórico';
      this.loading = false;
      this.history = [];
    };

    this.ipvaLicensingService.getByVehicle(this.vehicleId).subscribe({ next, error: err });
  }

  private applyFilters(list: VehicleIpvaLicensingPaymentHistory[]): VehicleIpvaLicensingPaymentHistory[] {
    let result = list;
    if (this.filterType === 'IPVA' || this.filterType === 'Licenciamento') {
      result = result.filter(h => h.paymentType === this.filterType);
    }
    if (this.filterYear != null && this.filterYear > 0) {
      result = result.filter(h => h.referenceYear === this.filterYear);
    }
    return result.sort((a, b) => b.referenceYear - a.referenceYear || (a.paymentType === 'IPVA' ? -1 : 1));
  }

  onFilterChange(): void {
    this.loadHistory();
  }

  goBack(): void {
    this.router.navigate(['/fleet/vehicles']);
  }

  openModalBank(): void {
    this.formBank = {
      vehicleId: this.vehicleId!,
      paymentType: 'IPVA',
      referenceYear: new Date().getFullYear(),
      amount: 0,
      documentNumber: '',
      notes: ''
    };
    this.bankSuccessMessage = null;
    this.boletoUrl = null;
    this.showModalBank = true;
  }

  closeModalBank(): void {
    this.showModalBank = false;
    this.loadHistory();
  }

  submitBank(): void {
    if (!this.vehicleId || this.submittingBank) return;
    const payload: VehicleIpvaLicensingRequestPaymentPayload = {
      vehicleId: this.vehicleId,
      paymentType: this.formBank.paymentType!,
      referenceYear: this.formBank.referenceYear!,
      amount: Number(this.formBank.amount) || 0,
      documentNumber: this.formBank.documentNumber || undefined,
      notes: this.formBank.notes || undefined
    };
    this.submittingBank = true;
    this.ipvaLicensingService.requestPayment(payload).subscribe({
      next: res => {
        this.submittingBank = false;
        this.bankSuccessMessage = res.message;
        this.boletoUrl = res.boletoUrl || null;
        if (res.history) this.history = [res.history, ...this.history];
      },
      error: () => (this.submittingBank = false)
    });
  }

  openModalManual(): void {
    this.formManual = {
      vehicleId: this.vehicleId!,
      paymentType: 'IPVA',
      referenceYear: new Date().getFullYear(),
      amount: 0,
      paymentDate: new Date().toISOString().slice(0, 10),
      documentNumber: '',
      notes: ''
    };
    this.showModalManual = true;
  }

  closeModalManual(): void {
    this.showModalManual = false;
    this.loadHistory();
  }

  submitManual(): void {
    if (!this.vehicleId || this.submittingManual) return;
    const payload: VehicleIpvaLicensingRecordManualPayload = {
      vehicleId: this.vehicleId,
      paymentType: this.formManual.paymentType!,
      referenceYear: this.formManual.referenceYear!,
      amount: Number(this.formManual.amount) || 0,
      paymentDate: this.formManual.paymentDate!,
      documentNumber: this.formManual.documentNumber || undefined,
      notes: this.formManual.notes || undefined
    };
    this.submittingManual = true;
    this.ipvaLicensingService.recordManual(payload).subscribe({
      next: () => {
        this.submittingManual = false;
        this.closeModalManual();
      },
      error: () => (this.submittingManual = false)
    });
  }

  onActionClick(data: { action: string; item: any }): void {
    const item = data.item as VehicleIpvaLicensingPaymentHistory;
    if (data.action === 'view') {
      this.router.navigate(['/fleet/vehicles', this.vehicleId, 'ipva-licensing', item.id]);
    } else if (data.action === 'refresh' && item.externalReference) {
      this.ipvaLicensingService.refreshStatus(item.id).subscribe({
        next: res => {
          if (res.updated) this.loadHistory();
        }
      });
    }
  }

  getTableData(): (VehicleIpvaLicensingPaymentHistory & { _actions?: TableAction[] })[] {
    return this.history.map(item => {
      const actions: TableAction[] = [{ label: 'Ver', icon: 'fas fa-eye', action: 'view', class: 'btn-view' }];
      if (item.externalReference) {
        actions.push({ label: 'Atualizar status', icon: 'fas fa-sync', action: 'refresh', class: 'btn-edit' });
      }
      return { ...item, _actions: actions };
    });
  }
}
